import { useState, useCallback, useEffect } from 'react'
import { GripVertical } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ResizableDividerProps {
  onResize: (delta: number) => void
  onResizeEnd?: () => void
}

export function ResizableDivider({ onResize, onResizeEnd }: ResizableDividerProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  useEffect(() => {
    if (!isDragging) return

    let lastX: number | null = null

    const handleMouseMove = (e: MouseEvent) => {
      if (lastX !== null) {
        const delta = e.clientX - lastX
        onResize(delta)
      }
      lastX = e.clientX
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      onResizeEnd?.()
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    // Add cursor style to body while dragging
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, onResize, onResizeEnd])

  return (
    <div
      className={cn(
        'relative w-px bg-border flex-shrink-0 group',
        'cursor-col-resize'
      )}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => !isDragging && setIsHovered(false)}
    >
      {/* Wider hit area */}
      <div className="absolute inset-y-0 -left-1 -right-1 z-10" />

      {/* Visual indicator on hover/drag */}
      <div
        className={cn(
          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
          'w-4 h-8 rounded-sm flex items-center justify-center',
          'transition-all duration-150',
          (isHovered || isDragging)
            ? 'bg-muted opacity-100'
            : 'opacity-0'
        )}
      >
        <GripVertical
          className={cn(
            'w-3 h-3 transition-colors',
            isDragging ? 'text-foreground' : 'text-muted-foreground'
          )}
          strokeWidth={1.5}
        />
      </div>

      {/* Highlight line on hover/drag */}
      <div
        className={cn(
          'absolute inset-y-0 left-0 w-px transition-colors',
          (isHovered || isDragging) ? 'bg-foreground/20' : 'bg-transparent'
        )}
      />
    </div>
  )
}
